import React, { useState, useEffect, Fragment } from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Collapse,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ChevronLeft as ChevronLeftIcon,
  Timer as TimerIcon,
  Autorenew as AutorenewIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Assignment as AssignmentIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FolderOutlined as FolderIcon,
  WorkspacesOutlined as WorkspaceIcon,
  StarOutlined as StarIcon,
  PeopleOutline as PeopleIcon,
  Add as AddIcon,
  AllInclusive as AllIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CreateNewFolder as CreateNewFolderIcon,
  Share as ShareIcon,
  DrawOutlined as DrawOutlinedIcon,
  DashboardOutlined as DashboardIcon,
  NotificationsActiveOutlined as NotificationsActiveIcon,
  HistoryOutlined as HistoryIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  fetchWorkspaces,
  fetchFolders,
  fetchSmartViews,
  fetchSmartViewCounts,
  createWorkspace,
  createFolder,
  setActiveWorkspace,
  setActiveFolder,
  setActiveSmartViewAction,
  setActiveMonitoringView,
  renameWorkspace,
  deleteWorkspace,
  renameFolder,
  deleteFolder,
  moveFolder,
  moveContract,
} from '../../../redux/actions/contractAction';
import { useSnackbar } from 'notistack';
import { backendClient } from '../../../api/backend';
import ShareWorkspaceDialog from './ShareWorkspaceDialog';

// Pre-built system smart views (fallback when API returns none)
const SYSTEM_SMART_VIEWS = [
  { id: 'expiring_30', icon: 'Timer', labelKey: 'repository.sidebar.expiringIn30', filter_params: { smartView: 'expiring_30' }, is_system: true },
  { id: 'auto_renewing', icon: 'Autorenew', labelKey: 'repository.sidebar.autoRenewing', filter_params: { smartView: 'auto_renewing' }, is_system: true },
  { id: 'high_value', icon: 'TrendingUp', labelKey: 'repository.sidebar.highValue', filter_params: { smartView: 'high_value' }, is_system: true },
  { id: 'unsigned_7', icon: 'DrawOutlined', labelKey: 'repository.sidebar.unsigned7', filter_params: { smartView: 'unsigned_7' }, is_system: true },
  { id: 'my_approvals', icon: 'Assignment', labelKey: 'repository.sidebar.myApprovals', filter_params: { smartView: 'my_approvals' }, is_system: true },
];

// Map smart-view ID → translation key (used when backend response omits labelKey)
const SMART_VIEW_LABEL_KEYS = {
  expiring_30: 'repository.sidebar.expiringIn30',
  auto_renewing: 'repository.sidebar.autoRenewing',
  high_value: 'repository.sidebar.highValue',
  unsigned_7: 'repository.sidebar.unsigned7',
  my_approvals: 'repository.sidebar.myApprovals',
  regulatory_alert: 'repository.sidebar.regulatoryAlert',
};

// Map icon name strings to MUI icon components
const ICON_MAP = {
  Timer: TimerIcon,
  Autorenew: AutorenewIcon,
  TrendingUp: TrendingUpIcon,
  Warning: WarningIcon,
  Assignment: AssignmentIcon,
  DrawOutlined: DrawOutlinedIcon,
  Star: StarIcon,
  Visibility: VisibilityIcon,
  Filter: FilterIcon,
};

const getSmartViewIcon = (iconName) => {
  return ICON_MAP[iconName] || StarIcon;
};

// ── Recursive folder tree ──────────────────────────────────────────────

const FolderTree = ({
  folders, level = 0, activeFolder, onFolderClick, goldAccent, theme,
  onContextMenu, renamingItem, renameValue, setRenameValue, onSaveRename, onCancelRename,
  dragOverId, setDragOverId, onMoveFolder, onDropContract, workspaceId,
}) => {
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (folderId, e) => {
    e.stopPropagation();
    setExpanded(prev => ({ ...prev, [folderId]: !prev[folderId] }));
  };

  if (!folders || folders.length === 0) return null;

  return folders.map(folder => {
    const hasChildren = folder.children && folder.children.length > 0;
    const isActive = activeFolder === folder.id;
    const isExpanded = expanded[folder.id];
    const isRenaming = renamingItem?.type === 'folder' && renamingItem?.id === folder.id;

    return (
      <Fragment key={folder.id}>
        <ListItemButton
          onClick={() => onFolderClick(folder)}
          onContextMenu={(e) => onContextMenu(e, 'folder', folder, workspaceId)}
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData('application/folder-id', String(folder.id));
            e.dataTransfer.setData('application/workspace-id', String(workspaceId || ''));
            e.dataTransfer.effectAllowed = 'move';
          }}
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverId(folder.id); }}
          onDragLeave={(e) => { e.stopPropagation(); setDragOverId(null); }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const draggedFolderId = e.dataTransfer.getData('application/folder-id');
            const wsId = e.dataTransfer.getData('application/workspace-id');
            if (draggedFolderId && draggedFolderId !== String(folder.id)) {
              onMoveFolder(draggedFolderId, folder.id, wsId);
            } else {
              const contractIdsRaw = e.dataTransfer.getData('application/contract-ids');
              const contractId = e.dataTransfer.getData('application/contract-id');
              let ids = [];
              if (contractIdsRaw) {
                try { ids = JSON.parse(contractIdsRaw); } catch { ids = []; }
              }
              if (ids.length === 0 && contractId) ids = [contractId];
              if (ids.length > 0 && onDropContract) {
                onDropContract(ids, { workspace_id: folder.workspace_id || workspaceId, folder_id: folder.id });
              }
            }
            setDragOverId(null);
          }}
          sx={{
            pl: 2 + level * 2,
            py: 0.5,
            mx: 1,
            borderRadius: '6px',
            mb: 0.25,
            minHeight: 32,
            backgroundColor: isActive ? alpha(goldAccent, 0.12) : 'transparent',
            border: dragOverId === folder.id ? `2px dashed ${goldAccent}` : '2px solid transparent',
            transition: 'border 0.15s',
            '&:hover': {
              backgroundColor: isActive
                ? alpha(goldAccent, 0.18)
                : alpha(theme.palette.action.hover, 0.06),
            },
          }}
        >
          {hasChildren && (
            <IconButton
              size="small"
              onClick={(e) => toggleExpand(folder.id, e)}
              sx={{ p: 0, mr: 0.5, minWidth: 20 }}
            >
              {isExpanded ? (
                <ExpandLessIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
              )}
            </IconButton>
          )}
          {!hasChildren && <Box sx={{ width: 20, mr: 0.5 }} />}
          <FolderIcon
            sx={{
              fontSize: 16,
              mr: 1,
              color: isActive ? goldAccent : theme.palette.text.secondary,
            }}
          />
          {isRenaming ? (
            <TextField
              size="small"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onSaveRename(); if (e.key === 'Escape') onCancelRename(); }}
              onBlur={onSaveRename}
              autoFocus
              onClick={(e) => e.stopPropagation()}
              sx={{ flex: 1, '& .MuiInputBase-root': { fontSize: '0.78rem', height: 28 }, '& .MuiInputBase-input': { py: 0.25, px: 0.5 } }}
            />
          ) : (
            <ListItemText
              primary={folder.name}
              primaryTypographyProps={{
                fontSize: '0.78rem',
                fontWeight: isActive ? 600 : 400,
                color: isActive ? goldAccent : theme.palette.text.primary,
                noWrap: true,
              }}
            />
          )}
          {!isRenaming && folder.contract_count > 0 && (
            <Chip
              label={folder.contract_count}
              size="small"
              sx={{
                height: 18,
                fontSize: '0.65rem',
                bgcolor: alpha(theme.palette.text.secondary, 0.1),
                color: theme.palette.text.secondary,
              }}
            />
          )}
        </ListItemButton>
        {hasChildren && (
          <Collapse in={isExpanded}>
            <FolderTree
              folders={folder.children}
              level={level + 1}
              activeFolder={activeFolder}
              onFolderClick={onFolderClick}
              goldAccent={goldAccent}
              theme={theme}
              onContextMenu={onContextMenu}
              renamingItem={renamingItem}
              renameValue={renameValue}
              setRenameValue={setRenameValue}
              onSaveRename={onSaveRename}
              onCancelRename={onCancelRename}
              dragOverId={dragOverId}
              setDragOverId={setDragOverId}
              onMoveFolder={onMoveFolder}
              onDropContract={onDropContract}
              workspaceId={workspaceId}
            />
          </Collapse>
        )}
      </Fragment>
    );
  });
};

// ── Main sidebar component ──────────────────────────────────────────────

const RepositorySidebar = ({ onFilterChange, onClose }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isDark = theme.palette.mode === 'dark';
  const goldAccent = '#c9a96e';
  const isDashboardRoute = location.pathname === '/contract/dashboard';

  // Redux state
  const workspaces = useSelector(state => state.contract?.workspaces || []);
  const folders = useSelector(state => state.contract?.folders || []);
  const smartViews = useSelector(state => state.contract?.smartViews || []);
  const activeWorkspace = useSelector(state => state.contract?.activeWorkspace);
  const activeFolder = useSelector(state => state.contract?.activeFolder);
  const activeSmartView = useSelector(state => state.contract?.activeSmartView);
  const smartViewCounts = useSelector(state => state.contract?.smartViewCounts || {});
  const loadingWorkspaces = useSelector(state => state.contract?.loadingWorkspaces || false);
  const activeMonitoringView = useSelector(state => state.contract?.activeMonitoringView);

  // Section collapse state
  const [smartViewsOpen, setSmartViewsOpen] = useState(true);
  const [workspacesOpen, setWorkspacesOpen] = useState(true);
  const [partiesOpen, setPartiesOpen] = useState(false);
  const [monitoringOpen, setMonitoringOpen] = useState(true);
  const [expandedWorkspaces, setExpandedWorkspaces] = useState({});

  // Parties state
  const [parties, setParties] = useState([]);
  const [partiesLoading, setPartiesLoading] = useState(false);
  const [selectedPartyId, setSelectedPartyId] = useState(null);

  // Dialog state
  const [createWorkspaceOpen, setCreateWorkspaceOpen] = useState(false);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [createFolderWorkspaceId, setCreateFolderWorkspaceId] = useState(null);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [newWorkspaceDesc, setNewWorkspaceDesc] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [dialogLoading, setDialogLoading] = useState(false);

  // Context menu, rename, delete, drag-drop state
  const [contextMenu, setContextMenu] = useState(null);
  const [renamingItem, setRenamingItem] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [createFolderParentId, setCreateFolderParentId] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareWorkspace, setShareWorkspace] = useState(null);

  // Fetch data on mount
  useEffect(() => {
    dispatch(fetchWorkspaces());
    dispatch(fetchSmartViews());
    dispatch(fetchSmartViewCounts());

    // Fetch parties
    setPartiesLoading(true);
    backendClient
      .getDashboardTopCounterparties()
      .then((data) => setParties(data?.parties || []))
      .catch(() => setParties([]))
      .finally(() => setPartiesLoading(false));
  }, [dispatch]);

  // Fetch folders when a workspace is expanded
  const handleToggleWorkspace = (workspace) => {
    const isExpanding = !expandedWorkspaces[workspace.id];
    setExpandedWorkspaces(prev => ({
      ...prev,
      [workspace.id]: isExpanding,
    }));
    if (isExpanding) {
      dispatch(fetchFolders(workspace.id));
    }
  };

  // Determine which smart views to display (regulatory_alert moved to Monitoring section)
  const rawSmartViews = smartViews.length > 0 ? smartViews : SYSTEM_SMART_VIEWS;
  const displaySmartViews = rawSmartViews.filter(v =>
    v.id !== 'regulatory_alert' &&
    (v.name || '').toLowerCase() !== 'regulatory alert' &&
    !(v.filter_params?.has_alerts)
  );

  // ── Handlers ────────────────────────────────────────────────────────

  const handleSmartViewClick = (view) => {
    const viewId = view.id;
    // The backend filter expects the smart view key (e.g. "unsigned_7"), not the DB UUID
    const smartViewKey = view.filter_params?.smartView || viewId;
    dispatch(setActiveMonitoringView(null));
    if (activeSmartView === viewId) {
      // Deselect
      dispatch(setActiveSmartViewAction(null));
      dispatch(setActiveWorkspace(null));
      dispatch(setActiveFolder(null));
      setSelectedPartyId(null);
      if (isDashboardRoute) {
        navigate('/contract');
      } else {
        onFilterChange(null);
      }
    } else {
      dispatch(setActiveSmartViewAction(viewId, smartViewKey));
      dispatch(setActiveWorkspace(null));
      dispatch(setActiveFolder(null));
      setSelectedPartyId(null);
      if (isDashboardRoute) {
        navigate('/contract');
      } else {
        onFilterChange({ smartView: smartViewKey });
      }
    }
  };

  const handleWorkspaceClick = (workspace) => {
    dispatch(setActiveMonitoringView(null));
    if (activeWorkspace === workspace.id) {
      dispatch(setActiveWorkspace(null));
      dispatch(setActiveFolder(null));
      dispatch(setActiveSmartViewAction(null));
      setSelectedPartyId(null);
      if (isDashboardRoute) {
        navigate('/contract');
      } else {
        onFilterChange(null);
      }
    } else {
      dispatch(setActiveWorkspace(workspace.id));
      dispatch(setActiveFolder(null));
      dispatch(setActiveSmartViewAction(null));
      setSelectedPartyId(null);
      if (isDashboardRoute) {
        navigate('/contract');
      } else {
        onFilterChange({ workspace_id: workspace.id });
      }
    }
  };

  const handleFolderClick = (folder) => {
    dispatch(setActiveMonitoringView(null));
    if (activeFolder === folder.id) {
      dispatch(setActiveFolder(null));
      setSelectedPartyId(null);
      if (isDashboardRoute) {
        navigate('/contract');
      } else if (activeWorkspace) {
        onFilterChange({ workspace_id: activeWorkspace });
      } else {
        onFilterChange(null);
      }
    } else {
      const workspaceId = folder.workspace_id || activeWorkspace;
      dispatch(setActiveWorkspace(workspaceId));
      dispatch(setActiveFolder(folder.id));
      dispatch(setActiveSmartViewAction(null));
      setSelectedPartyId(null);
      if (isDashboardRoute) {
        navigate('/contract');
      } else {
        onFilterChange({ workspace_id: workspaceId, folder_id: folder.id });
      }
    }
  };

  const handleMonitoringClick = (view) => {
    if (activeMonitoringView === view) {
      dispatch(setActiveMonitoringView(null));
    } else {
      dispatch(setActiveMonitoringView(view));
      dispatch(setActiveWorkspace(null));
      dispatch(setActiveFolder(null));
      dispatch(setActiveSmartViewAction(null));
      setSelectedPartyId(null);
      if (isDashboardRoute) {
        navigate('/contract');
      }
    }
  };

  const handleAllContracts = () => {
    dispatch(setActiveWorkspace(null));
    dispatch(setActiveFolder(null));
    dispatch(setActiveSmartViewAction(null));
    dispatch(setActiveMonitoringView(null));
    setSelectedPartyId(null);
    if (isDashboardRoute) {
      navigate('/contract');
    } else {
      onFilterChange(null);
    }
  };

  const handlePartyClick = (party) => {
    if (selectedPartyId === party.party_id) {
      // Deselect
      setSelectedPartyId(null);
      dispatch(setActiveWorkspace(null));
      dispatch(setActiveFolder(null));
      dispatch(setActiveSmartViewAction(null));
      if (isDashboardRoute) {
        navigate('/contract');
      } else {
        onFilterChange(null);
      }
    } else {
      setSelectedPartyId(party.party_id);
      dispatch(setActiveWorkspace(null));
      dispatch(setActiveFolder(null));
      dispatch(setActiveSmartViewAction(null));
      if (isDashboardRoute) {
        navigate('/contract');
      } else {
        onFilterChange({ party_ids: [party.party_id] });
      }
    }
  };

  const handleDashboardClick = () => {
    navigate('/contract/dashboard');
  };

  const handleCreateWorkspace = async () => {
    if (!newWorkspaceName.trim()) return;
    setDialogLoading(true);
    try {
      await dispatch(createWorkspace({
        name: newWorkspaceName.trim(),
        description: newWorkspaceDesc.trim(),
      }));
      setCreateWorkspaceOpen(false);
      setNewWorkspaceName('');
      setNewWorkspaceDesc('');
    } catch (e) {
      console.error('Failed to create workspace:', e);
    }
    setDialogLoading(false);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !createFolderWorkspaceId) return;
    setDialogLoading(true);
    try {
      await dispatch(createFolder({
        name: newFolderName.trim(),
        workspace_id: createFolderWorkspaceId,
        parent_id: createFolderParentId || undefined,
      }));
      // Refresh folders for the workspace
      dispatch(fetchFolders(createFolderWorkspaceId));
      setCreateFolderOpen(false);
      setNewFolderName('');
      setCreateFolderWorkspaceId(null);
      setCreateFolderParentId(null);
    } catch (e) {
      console.error('Failed to create folder:', e);
    }
    setDialogLoading(false);
  };

  const openCreateFolder = (workspaceId, e) => {
    e.stopPropagation();
    setCreateFolderWorkspaceId(workspaceId);
    setCreateFolderOpen(true);
  };

  // Context menu
  const handleContextMenu = (event, type, item, workspaceId = null) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({ mouseX: event.clientX, mouseY: event.clientY, type, item, workspaceId });
  };
  const handleCloseContextMenu = () => setContextMenu(null);

  // Rename
  const handleStartRename = () => {
    if (!contextMenu) return;
    setRenamingItem({ type: contextMenu.type, id: contextMenu.item.id, workspaceId: contextMenu.workspaceId });
    setRenameValue(contextMenu.item.name);
    handleCloseContextMenu();
  };
  const handleSaveRename = async () => {
    if (!renamingItem || !renameValue.trim()) { setRenamingItem(null); return; }
    if (renamingItem.type === 'workspace') {
      await dispatch(renameWorkspace(renamingItem.id, renameValue.trim()));
    } else {
      await dispatch(renameFolder(renamingItem.id, renameValue.trim(), renamingItem.workspaceId));
    }
    setRenamingItem(null);
    setRenameValue('');
  };
  const handleCancelRename = () => { setRenamingItem(null); setRenameValue(''); };

  // Delete
  const handleStartDelete = () => {
    if (!contextMenu) return;
    setDeleteConfirm({ type: contextMenu.type, item: contextMenu.item, workspaceId: contextMenu.workspaceId });
    handleCloseContextMenu();
  };
  const handleConfirmDelete = async () => {
    if (!deleteConfirm) return;
    if (deleteConfirm.type === 'workspace') {
      await dispatch(deleteWorkspace(deleteConfirm.item.id));
      if (activeWorkspace === deleteConfirm.item.id) {
        dispatch(setActiveWorkspace(null));
        dispatch(setActiveFolder(null));
        onFilterChange(null);
      }
    } else {
      await dispatch(deleteFolder(deleteConfirm.item.id, deleteConfirm.workspaceId));
      if (activeFolder === deleteConfirm.item.id) dispatch(setActiveFolder(null));
    }
    setDeleteConfirm(null);
  };

  // Subfolder from context menu
  const handleCreateSubfolder = () => {
    if (!contextMenu || contextMenu.type !== 'folder') return;
    setCreateFolderWorkspaceId(contextMenu.workspaceId || contextMenu.item.workspace_id);
    setCreateFolderParentId(contextMenu.item.id);
    setCreateFolderOpen(true);
    handleCloseContextMenu();
  };

  // Drag and drop folder
  const handleMoveFolder = async (folderId, newParentId, workspaceId) => {
    await dispatch(moveFolder(folderId, newParentId, workspaceId));
  };

  // Drop contract(s) onto a workspace or folder
  const { enqueueSnackbar } = useSnackbar();
  const handleDropContract = async (contractIds, target) => {
    if (!contractIds || contractIds.length === 0) return;
    try {
      await Promise.all(
        contractIds.map((id) =>
          dispatch(moveContract(id, target.workspace_id, target.folder_id))
        )
      );
      enqueueSnackbar(
        contractIds.length === 1
          ? t('moveContract.successOne', 'Contract moved')
          : t('moveContract.successMany', '{{count}} contracts moved', { count: contractIds.length }),
        { variant: 'success' }
      );
      // Refresh folder counts for affected workspace
      if (target.workspace_id) dispatch(fetchFolders(target.workspace_id));
      dispatch(fetchWorkspaces());
      // Refresh current view
      if (activeWorkspace || activeFolder) {
        // Caller's parent will refresh via existing filter logic on next interaction
      }
    } catch (e) {
      enqueueSnackbar(t('moveContract.error', 'Failed to move'), { variant: 'error' });
    }
  };

  // Share workspace or folder
  const handleShareWorkspace = () => {
    setShareWorkspace({ ...contextMenu.item, _shareType: contextMenu.type });
    setShareDialogOpen(true);
    handleCloseContextMenu();
  };

  // Get folders for a specific workspace
  const getFoldersForWorkspace = (workspaceId) => {
    return folders.filter(f => f.workspace_id === workspaceId || !f.workspace_id);
  };

  const noActiveSelection = !activeWorkspace && !activeFolder && !activeSmartView && !selectedPartyId && !isDashboardRoute;

  return (
    <Box
      sx={{
        width: 240,
        minWidth: 240,
        height: '100%',
        borderRight: `1px solid ${theme.palette.divider}`,
        backgroundColor: isDark ? alpha('#111', 0.6) : alpha(theme.palette.background.paper, 0.95),
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: '0.8rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: theme.palette.text.secondary,
          }}
        >
          {t('repository.sidebar.title', 'Repository')}
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: theme.palette.text.secondary }}>
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>

        {/* Dashboard button */}
        <ListItemButton
          onClick={handleDashboardClick}
          sx={{
            px: 2,
            py: 0.75,
            mx: 1,
            borderRadius: '8px',
            mb: 0.5,
            backgroundColor: isDashboardRoute ? alpha(goldAccent, 0.12) : 'transparent',
            '&:hover': {
              backgroundColor: isDashboardRoute
                ? alpha(goldAccent, 0.18)
                : alpha(theme.palette.action.hover, 0.06),
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <DashboardIcon
              sx={{
                fontSize: 18,
                color: isDashboardRoute ? goldAccent : theme.palette.text.secondary,
              }}
            />
          </ListItemIcon>
          <ListItemText
            primary={t('repository.sidebar.dashboard', 'Dashboard')}
            primaryTypographyProps={{
              fontSize: '0.82rem',
              fontWeight: isDashboardRoute ? 600 : 400,
              color: isDashboardRoute ? goldAccent : theme.palette.text.primary,
            }}
          />
        </ListItemButton>

        {/* "All Contracts" button (also acts as "unfile" drop target) */}
        <ListItemButton
          onClick={handleAllContracts}
          onDragOver={(e) => {
            if (e.dataTransfer.types.includes('application/contract-id')) {
              e.preventDefault();
              setDragOverId('all');
            }
          }}
          onDragLeave={() => setDragOverId(null)}
          onDrop={(e) => {
            e.preventDefault();
            const contractIdsRaw = e.dataTransfer.getData('application/contract-ids');
            const contractId = e.dataTransfer.getData('application/contract-id');
            let ids = [];
            if (contractIdsRaw) {
              try { ids = JSON.parse(contractIdsRaw); } catch { ids = []; }
            }
            if (ids.length === 0 && contractId) ids = [contractId];
            if (ids.length > 0) {
              handleDropContract(ids, { workspace_id: null, folder_id: null });
            }
            setDragOverId(null);
          }}
          sx={{
            px: 2,
            py: 0.75,
            mx: 1,
            borderRadius: '8px',
            mb: 0.5,
            backgroundColor: noActiveSelection ? alpha(goldAccent, 0.12) : 'transparent',
            border: dragOverId === 'all'
              ? `2px dashed ${goldAccent}`
              : '2px solid transparent',
            transition: 'border 0.15s',
            '&:hover': {
              backgroundColor: noActiveSelection
                ? alpha(goldAccent, 0.18)
                : alpha(theme.palette.action.hover, 0.06),
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <AllIcon
              sx={{
                fontSize: 18,
                color: noActiveSelection ? goldAccent : theme.palette.text.secondary,
              }}
            />
          </ListItemIcon>
          <ListItemText
            primary={t('repository.sidebar.allContracts', 'All Contracts')}
            primaryTypographyProps={{
              fontSize: '0.82rem',
              fontWeight: noActiveSelection ? 600 : 400,
              color: noActiveSelection ? goldAccent : theme.palette.text.primary,
            }}
          />
        </ListItemButton>

        <Divider sx={{ my: 1, mx: 2 }} />

        {/* ── Smart Views Section ────────────────────────────────────── */}
        <ListItemButton
          onClick={() => setSmartViewsOpen(!smartViewsOpen)}
          sx={{
            px: 2,
            py: 0.75,
            '&:hover': { backgroundColor: alpha(theme.palette.action.hover, 0.04) },
          }}
        >
          <ListItemText
            primary={t('repository.sidebar.smartViews', 'Smart Views')}
            primaryTypographyProps={{
              fontSize: '0.75rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: theme.palette.text.secondary,
            }}
          />
          {smartViewsOpen ? (
            <ExpandLessIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
          )}
        </ListItemButton>

        <Collapse in={smartViewsOpen}>
          <List dense disablePadding>
            {displaySmartViews.map((view) => {
              const iconName = view.icon || 'Star';
              const Icon = getSmartViewIcon(iconName);
              const isActive = activeSmartView === view.id;
              const labelKey = view.labelKey || SMART_VIEW_LABEL_KEYS[view.id];
              const label = labelKey ? t(labelKey, view.name || view.id) : (view.name || view.id);
              return (
                <ListItemButton
                  key={view.id}
                  onClick={() => handleSmartViewClick(view)}
                  sx={{
                    px: 2,
                    py: 0.75,
                    mx: 1,
                    borderRadius: '8px',
                    mb: 0.25,
                    backgroundColor: isActive ? alpha(goldAccent, 0.12) : 'transparent',
                    '&:hover': {
                      backgroundColor: isActive
                        ? alpha(goldAccent, 0.18)
                        : alpha(theme.palette.action.hover, 0.06),
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Icon
                      sx={{
                        fontSize: 18,
                        color: isActive ? goldAccent : theme.palette.text.secondary,
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{
                      fontSize: '0.78rem',
                      fontWeight: isActive ? 600 : 400,
                      color: isActive ? goldAccent : theme.palette.text.primary,
                      noWrap: true,
                    }}
                  />
                  {smartViewCounts[view.id] > 0 && (
                    <Chip
                      label={smartViewCounts[view.id]}
                      size="small"
                      sx={{
                        height: 20,
                        minWidth: 20,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        backgroundColor: isActive ? alpha(goldAccent, 0.2) : alpha(theme.palette.text.secondary, 0.1),
                        color: isActive ? goldAccent : theme.palette.text.secondary,
                        '& .MuiChip-label': { px: 0.75 },
                      }}
                    />
                  )}
                </ListItemButton>
              );
            })}
          </List>
        </Collapse>

        {/* ── Monitoring Section ─────────────────────────────────────── */}
        <Box sx={{ mb: 0.5 }}>
          <ListItemButton
            onClick={() => setMonitoringOpen(!monitoringOpen)}
            sx={{
              py: 0.75,
              px: 2,
              '&:hover': { backgroundColor: alpha(theme.palette.action.hover, 0.06) },
            }}
          >
            <ListItemText
              primary={t('repository.sidebar.monitoring', 'Monitoring')}
              primaryTypographyProps={{
                fontSize: '0.7rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: theme.palette.text.secondary,
              }}
            />
            {monitoringOpen ? (
              <ExpandLessIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
            )}
          </ListItemButton>
          <Collapse in={monitoringOpen}>
            <List disablePadding>
              <ListItemButton
                onClick={() => handleMonitoringClick('regulatory_alerts')}
                sx={{
                  pl: 2,
                  py: 0.5,
                  mx: 1,
                  borderRadius: '6px',
                  mb: 0.25,
                  minHeight: 32,
                  backgroundColor: activeMonitoringView === 'regulatory_alerts' ? alpha(goldAccent, 0.12) : 'transparent',
                  '&:hover': {
                    backgroundColor: activeMonitoringView === 'regulatory_alerts'
                      ? alpha(goldAccent, 0.18)
                      : alpha(theme.palette.action.hover, 0.06),
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <NotificationsActiveIcon
                    sx={{
                      fontSize: 16,
                      color: activeMonitoringView === 'regulatory_alerts' ? goldAccent : theme.palette.text.secondary,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={t('repository.sidebar.regulatoryAlerts', 'Regulatory Alerts')}
                  primaryTypographyProps={{
                    fontSize: '0.78rem',
                    fontWeight: activeMonitoringView === 'regulatory_alerts' ? 600 : 400,
                    color: activeMonitoringView === 'regulatory_alerts' ? goldAccent : theme.palette.text.primary,
                    noWrap: true,
                  }}
                />
              </ListItemButton>
              <ListItemButton
                onClick={() => handleMonitoringClick('contract_lifecycle')}
                sx={{
                  pl: 2,
                  py: 0.5,
                  mx: 1,
                  borderRadius: '6px',
                  mb: 0.25,
                  minHeight: 32,
                  backgroundColor: activeMonitoringView === 'contract_lifecycle' ? alpha(goldAccent, 0.12) : 'transparent',
                  '&:hover': {
                    backgroundColor: activeMonitoringView === 'contract_lifecycle'
                      ? alpha(goldAccent, 0.18)
                      : alpha(theme.palette.action.hover, 0.06),
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <HistoryIcon
                    sx={{
                      fontSize: 16,
                      color: activeMonitoringView === 'contract_lifecycle' ? goldAccent : theme.palette.text.secondary,
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={t('repository.sidebar.contractLifecycle', 'Contract Life Cycle')}
                  primaryTypographyProps={{
                    fontSize: '0.78rem',
                    fontWeight: activeMonitoringView === 'contract_lifecycle' ? 600 : 400,
                    color: activeMonitoringView === 'contract_lifecycle' ? goldAccent : theme.palette.text.primary,
                    noWrap: true,
                  }}
                />
              </ListItemButton>
            </List>
          </Collapse>
        </Box>

        <Divider sx={{ my: 1, mx: 2 }} />

        {/* ── Workspaces Section ─────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ListItemButton
            onClick={() => setWorkspacesOpen(!workspacesOpen)}
            sx={{
              px: 2,
              py: 0.75,
              flex: 1,
              '&:hover': { backgroundColor: alpha(theme.palette.action.hover, 0.04) },
            }}
          >
            <ListItemText
              primary={t('repository.sidebar.workspaces', 'Workspaces')}
              primaryTypographyProps={{
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                color: theme.palette.text.secondary,
              }}
            />
            {workspacesOpen ? (
              <ExpandLessIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
            )}
          </ListItemButton>
          <Tooltip title={t('repository.sidebar.createWorkspace', 'New Workspace')}>
            <IconButton
              size="small"
              onClick={() => setCreateWorkspaceOpen(true)}
              sx={{ mr: 1, color: theme.palette.text.secondary }}
            >
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>

        <Collapse in={workspacesOpen}>
          {loadingWorkspaces ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} sx={{ color: theme.palette.text.secondary }} />
            </Box>
          ) : workspaces.length === 0 ? (
            <Box sx={{ px: 2, py: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.disabled,
                  fontSize: '0.72rem',
                  fontStyle: 'italic',
                }}
              >
                {t('repository.sidebar.noWorkspaces', 'No workspaces yet')}
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {workspaces.map((workspace) => {
                const isActive = activeWorkspace === workspace.id;
                const isExpanded = expandedWorkspaces[workspace.id];
                const workspaceFolders = getFoldersForWorkspace(workspace.id);

                return (
                  <Fragment key={workspace.id}>
                    <ListItemButton
                      onClick={() => handleWorkspaceClick(workspace)}
                      onContextMenu={(e) => handleContextMenu(e, 'workspace', workspace)}
                      onDragOver={(e) => {
                        if (e.dataTransfer.types.includes('application/contract-id')) {
                          e.preventDefault();
                          setDragOverId(`ws:${workspace.id}`);
                        }
                      }}
                      onDragLeave={() => setDragOverId(null)}
                      onDrop={(e) => {
                        e.preventDefault();
                        const contractIdsRaw = e.dataTransfer.getData('application/contract-ids');
                        const contractId = e.dataTransfer.getData('application/contract-id');
                        let ids = [];
                        if (contractIdsRaw) {
                          try { ids = JSON.parse(contractIdsRaw); } catch { ids = []; }
                        }
                        if (ids.length === 0 && contractId) ids = [contractId];
                        if (ids.length > 0) {
                          handleDropContract(ids, { workspace_id: workspace.id, folder_id: null });
                        }
                        setDragOverId(null);
                      }}
                      sx={{
                        px: 2,
                        py: 0.6,
                        mx: 1,
                        borderRadius: '8px',
                        mb: 0.25,
                        backgroundColor: isActive && !activeFolder ? alpha(goldAccent, 0.12) : 'transparent',
                        border: dragOverId === `ws:${workspace.id}`
                          ? `2px dashed ${goldAccent}`
                          : '2px solid transparent',
                        transition: 'border 0.15s',
                        '&:hover': {
                          backgroundColor: isActive && !activeFolder
                            ? alpha(goldAccent, 0.18)
                            : alpha(theme.palette.action.hover, 0.06),
                        },
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleWorkspace(workspace);
                        }}
                        sx={{ p: 0, mr: 0.5, minWidth: 20 }}
                      >
                        {isExpanded ? (
                          <ExpandLessIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                        ) : (
                          <ExpandMoreIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                        )}
                      </IconButton>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <WorkspaceIcon
                          sx={{
                            fontSize: 18,
                            color: isActive ? goldAccent : theme.palette.text.secondary,
                          }}
                        />
                      </ListItemIcon>
                      {renamingItem?.type === 'workspace' && renamingItem?.id === workspace.id ? (
                        <TextField
                          size="small"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleSaveRename(); if (e.key === 'Escape') handleCancelRename(); }}
                          onBlur={handleSaveRename}
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                          sx={{ flex: 1, '& .MuiInputBase-root': { fontSize: '0.78rem', height: 28 }, '& .MuiInputBase-input': { py: 0.25, px: 0.5 } }}
                        />
                      ) : (
                        <ListItemText
                          primary={workspace.name}
                          primaryTypographyProps={{
                            fontSize: '0.78rem',
                            fontWeight: isActive ? 600 : 400,
                            color: isActive && !activeFolder ? goldAccent : theme.palette.text.primary,
                            noWrap: true,
                          }}
                        />
                      )}
                      {!(renamingItem?.type === 'workspace' && renamingItem?.id === workspace.id) && workspace.contract_count > 0 && (
                        <Chip
                          label={workspace.contract_count}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: '0.65rem',
                            mr: 0.5,
                            bgcolor: alpha(theme.palette.text.secondary, 0.1),
                            color: theme.palette.text.secondary,
                          }}
                        />
                      )}
                      <Tooltip title={t('repository.sidebar.createFolder', 'New Folder')}>
                        <IconButton
                          size="small"
                          onClick={(e) => openCreateFolder(workspace.id, e)}
                          sx={{
                            p: 0.25,
                            color: theme.palette.text.secondary,
                            opacity: 0.6,
                            '&:hover': { opacity: 1 },
                          }}
                        >
                          <AddIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </ListItemButton>

                    {/* Folder tree inside workspace */}
                    <Collapse in={isExpanded}>
                      {workspaceFolders.length > 0 ? (
                        <FolderTree
                          folders={workspaceFolders}
                          level={1}
                          activeFolder={activeFolder}
                          onFolderClick={handleFolderClick}
                          goldAccent={goldAccent}
                          theme={theme}
                          onContextMenu={handleContextMenu}
                          renamingItem={renamingItem}
                          renameValue={renameValue}
                          setRenameValue={setRenameValue}
                          onSaveRename={handleSaveRename}
                          onCancelRename={handleCancelRename}
                          dragOverId={dragOverId}
                          setDragOverId={setDragOverId}
                          onMoveFolder={handleMoveFolder}
                          onDropContract={handleDropContract}
                          workspaceId={workspace.id}
                        />
                      ) : (
                        <Box sx={{ pl: 5, py: 0.5 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.disabled,
                              fontSize: '0.72rem',
                              fontStyle: 'italic',
                            }}
                          >
                            {t('repository.sidebar.noFolders', 'No folders')}
                          </Typography>
                        </Box>
                      )}
                    </Collapse>
                  </Fragment>
                );
              })}
            </List>
          )}
        </Collapse>

        <Divider sx={{ my: 1, mx: 2 }} />

        {/* ── Parties Section ────────────────────────────────────────── */}
        <ListItemButton
          onClick={() => setPartiesOpen(!partiesOpen)}
          sx={{
            px: 2,
            py: 0.75,
            '&:hover': { backgroundColor: alpha(theme.palette.action.hover, 0.04) },
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <PeopleIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
          </ListItemIcon>
          <ListItemText
            primary={t('repository.sidebar.parties', 'Parties')}
            primaryTypographyProps={{
              fontSize: '0.75rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              color: theme.palette.text.secondary,
            }}
          />
          {partiesOpen ? (
            <ExpandLessIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
          )}
        </ListItemButton>

        <Collapse in={partiesOpen}>
          {partiesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={18} sx={{ color: theme.palette.text.secondary }} />
            </Box>
          ) : parties.length === 0 ? (
            <Box sx={{ px: 2, py: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.disabled,
                  fontSize: '0.72rem',
                  fontStyle: 'italic',
                }}
              >
                {t('repository.sidebar.noParties', 'No counterparties yet')}
              </Typography>
            </Box>
          ) : (
            <List dense disablePadding>
              {parties.map((party) => {
                const isActive = selectedPartyId === party.party_id;
                return (
                  <ListItemButton
                    key={party.party_id}
                    onClick={() => handlePartyClick(party)}
                    sx={{
                      px: 2,
                      py: 0.5,
                      mx: 1,
                      borderRadius: '8px',
                      mb: 0.25,
                      backgroundColor: isActive ? alpha(goldAccent, 0.12) : 'transparent',
                      '&:hover': {
                        backgroundColor: isActive
                          ? alpha(goldAccent, 0.18)
                          : alpha(theme.palette.action.hover, 0.06),
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <PeopleIcon
                        sx={{
                          fontSize: 16,
                          color: isActive ? goldAccent : theme.palette.text.secondary,
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={party.name}
                      primaryTypographyProps={{
                        fontSize: '0.78rem',
                        fontWeight: isActive ? 600 : 400,
                        color: isActive ? goldAccent : theme.palette.text.primary,
                        noWrap: true,
                      }}
                    />
                    {party.contract_count > 0 && (
                      <Chip
                        label={party.contract_count}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.65rem',
                          bgcolor: isActive ? alpha(goldAccent, 0.2) : alpha(theme.palette.text.secondary, 0.1),
                          color: isActive ? goldAccent : theme.palette.text.secondary,
                          '& .MuiChip-label': { px: 0.75 },
                        }}
                      />
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Collapse>
      </Box>

      {/* ── Context Menu ─────────────────────────────────────────────── */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
        PaperProps={{ sx: { bgcolor: theme.palette.background.paper, borderRadius: '8px', minWidth: 180, boxShadow: theme.shadows[8] } }}
      >
        <MenuItem onClick={handleStartRename} sx={{ fontSize: '0.82rem', py: 0.75 }}>
          <EditIcon sx={{ fontSize: 16, mr: 1.5, color: theme.palette.text.secondary }} />
          {t('repository.sidebar.rename', 'Rename')}
        </MenuItem>
        <MenuItem onClick={handleShareWorkspace} sx={{ fontSize: '0.82rem', py: 0.75 }}>
          <ShareIcon sx={{ fontSize: 16, mr: 1.5, color: theme.palette.text.secondary }} />
          {t('repository.sidebar.share', 'Share')}
        </MenuItem>
        {contextMenu?.type === 'folder' && (
          <MenuItem onClick={handleCreateSubfolder} sx={{ fontSize: '0.82rem', py: 0.75 }}>
            <CreateNewFolderIcon sx={{ fontSize: 16, mr: 1.5, color: theme.palette.text.secondary }} />
            {t('repository.sidebar.newSubfolder', 'New Subfolder')}
          </MenuItem>
        )}
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={handleStartDelete} sx={{ fontSize: '0.82rem', py: 0.75, color: theme.palette.error.main }}>
          <DeleteIcon sx={{ fontSize: 16, mr: 1.5 }} />
          {t('repository.sidebar.delete', 'Delete')}
        </MenuItem>
      </Menu>

      {/* ── Delete Confirmation Dialog ──────────────────────────────── */}
      <Dialog
        open={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: theme.palette.background.paper, borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
          {t('repository.sidebar.confirmDelete', 'Confirm Delete')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.85rem', color: theme.palette.text.secondary }}>
            {deleteConfirm?.type === 'workspace'
              ? t('repository.sidebar.deleteWorkspaceMsg', 'Delete this workspace? Contracts will be unlinked, not deleted.')
              : t('repository.sidebar.deleteFolderMsg', 'Delete this folder and subfolders? Contracts will be moved to workspace root.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)} size="small">{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleConfirmDelete} variant="contained" size="small" color="error">
            {t('repository.sidebar.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Create Workspace Dialog ──────────────────────────────────── */}
      <Dialog
        open={createWorkspaceOpen}
        onClose={() => setCreateWorkspaceOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
          {t('repository.sidebar.createWorkspace', 'New Workspace')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={t('repository.sidebar.workspaceName', 'Name')}
            value={newWorkspaceName}
            onChange={(e) => setNewWorkspaceName(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateWorkspace()}
          />
          <TextField
            fullWidth
            size="small"
            label={t('repository.sidebar.workspaceDescription', 'Description')}
            value={newWorkspaceDesc}
            onChange={(e) => setNewWorkspaceDesc(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateWorkspaceOpen(false)} size="small">
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleCreateWorkspace}
            variant="contained"
            size="small"
            disabled={!newWorkspaceName.trim() || dialogLoading}
            sx={{
              bgcolor: goldAccent,
              '&:hover': { bgcolor: alpha(goldAccent, 0.85) },
            }}
          >
            {dialogLoading ? <CircularProgress size={18} /> : t('common.create', 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Create Folder Dialog ─────────────────────────────────────── */}
      <Dialog
        open={createFolderOpen}
        onClose={() => setCreateFolderOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.background.paper,
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle sx={{ fontSize: '0.95rem', fontWeight: 600 }}>
          {t('repository.sidebar.createFolder', 'New Folder')}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={t('repository.sidebar.folderName', 'Folder name')}
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            sx={{ mt: 1 }}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFolderOpen(false)} size="small">
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleCreateFolder}
            variant="contained"
            size="small"
            disabled={!newFolderName.trim() || dialogLoading}
            sx={{
              bgcolor: goldAccent,
              '&:hover': { bgcolor: alpha(goldAccent, 0.85) },
            }}
          >
            {dialogLoading ? <CircularProgress size={18} /> : t('common.create', 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Share Workspace Dialog ──────────────────────────────────── */}
      <ShareWorkspaceDialog
        open={shareDialogOpen}
        onClose={() => { setShareDialogOpen(false); setShareWorkspace(null); }}
        workspace={shareWorkspace}
      />
    </Box>
  );
};

export default RepositorySidebar;
